import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Progress, List, Button, Tag, message, Space, Badge, Modal, Form, Select, Collapse } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { processAPI, userAPI } from '../services/api';
import EditorComponent from '../components/EditorComponent';
import EditorReadOnly from '../components/EditorReadOnly';

const ProcessDetail = () => {
  const { id } = useParams();
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [editStageModalVisible, setEditStageModalVisible] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [editForm] = Form.useForm();
  const [stageUsers, setStageUsers] = useState({});
  const [stageContent, setStageContent] = useState({});
  const [editorData, setEditorData] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const editorRef = useRef(null);
  const navigate = useNavigate();

  // 获取流程详情
  const fetchProcessDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await processAPI.getProcess(id);
      setProcess(response.data);
      return response.data;
    } catch (error) {
      console.error('获取流程详情错误:', error);
      message.error('获取流程详情失败');
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  // 获取用户列表
  const fetchUsers = useCallback(async () => {
    try {
      const response = await userAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('获取用户列表错误:', error);
      message.error('获取用户列表失败');
    }
  }, []);

  // 获取流程详情和用户列表
  useEffect(() => {
    fetchProcessDetail();
    fetchUsers();
  }, [fetchProcessDetail, fetchUsers]);


  const handleEditStage = (stage) => {
    setCurrentStage(stage);
    editForm.resetFields();
    const userIds = stage.users ? stage.users.map(user => user.user_id) : [];
    
    let parsedContent = null;
    if (stage.content) {
      try {
        parsedContent = JSON.parse(stage.content);
      } catch (e) {
        parsedContent = null;
      }
    }
    setEditorData(parsedContent);
    setEditorReady(false); // 重置编辑器就绪状态
    
    editForm.setFieldsValue({
      users: userIds,
      content: stage.content || ''
    });
    setEditStageModalVisible(true);
  };

  // 处理模态框关闭
  const handleEditStageModalClose = () => {
    setEditStageModalVisible(false);
    setEditorReady(false); // 重置编辑器就绪状态
  };



  const handleEditStageSubmit = async (values) => {
    setLoading(true);
    try {
      const userData = localStorage.getItem('userInfo');
      const currentUserId = userData ? JSON.parse(userData).id : 1;
      
      let contentToSave = '';
      console.log('=== Debug: 保存阶段信息 ===');
      console.log('=== Debug: editorRef.current ===', editorRef.current);
      console.log('=== Debug: editorReady ===', editorReady);
      
      if (editorRef.current && editorReady) {
        console.log('=== Debug: Checking if editor is ready...');
        const isEditorReady = editorRef.current.isReady();
        console.log('=== Debug: editor is ready ===', isEditorReady);
        
        if (isEditorReady) {
          console.log('=== Debug: Calling getEditorData...');
          const editorContent = await editorRef.current.getEditorData();
          console.log('=== Debug: getEditorData result ===', editorContent);
          if (editorContent) {
            contentToSave = editorContent;
          }
        } else {
          console.log('=== Debug: Editor is not ready yet');
          // 等待编辑器就绪
          await new Promise(resolve => {
            const checkReady = setInterval(() => {
              if (editorRef.current && editorRef.current.isReady()) {
                clearInterval(checkReady);
                resolve();
              }
            }, 100);
            // 最多等待5秒
            setTimeout(() => {
              clearInterval(checkReady);
              resolve();
            }, 5000);
          });
          
          // 再次尝试获取内容
          if (editorRef.current) {
            const editorContent = await editorRef.current.getEditorData();
            if (editorContent) {
              contentToSave = editorContent;
            }
          }
        }
      } else {
        console.log('=== Debug: editorRef.current is null or editor not ready');
      }
      
      console.log('=== Debug: contentToSave ===', contentToSave);
      await processAPI.saveStageUsers(currentStage.id, values.users);
      await processAPI.saveStageContent(currentStage.id, contentToSave, currentUserId);
      
      setStageUsers(prev => ({
        ...prev,
        [currentStage.id]: values.users
      }));
      setStageContent(prev => ({
        ...prev,
        [currentStage.id]: contentToSave
      }));
      
      const updatedProcess = await fetchProcessDetail();
      
      // 更新 currentStage 以刷新 EditorReadOnly 显示
      if (currentStage && updatedProcess?.stages) {
        const updatedStage = updatedProcess.stages.find(s => s.id === currentStage.id);
        if (updatedStage) {
          setCurrentStage(updatedStage);
        }
      }
      
      message.success('阶段信息保存成功');
      handleEditStageModalClose();
    } catch (error) {
      console.error('保存阶段信息错误:', error);
      message.error('保存阶段信息失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理标记阶段用户完成
  const handleCompleteStageUser = async (stageUserId) => {
    setLoading(true);
    try {
      await processAPI.completeStageUser(stageUserId);
      // 重新获取流程详情，更新阶段用户状态
      await fetchProcessDetail();
      message.success('标记完成成功');
    } catch (error) {
      console.error('标记完成错误:', error);
      message.error('标记完成失败');
    } finally {
      setLoading(false);
    }
  };

  // 计算进度
  const calculateProgress = () => {
    if (!process || !process.stages) return 0;
    const totalStages = process.stages.length;
    const completedStages = process.stages.filter(stage => stage.status === 'completed').length;
    return Math.round((completedStages / totalStages) * 100);
  };

  const handleViewTask = (taskId) => {
    navigate(`/task/${taskId}`);
  };

  const renderEditorContent = (content) => {
    if (!content) return <span style={{ color: '#999' }}>未填写</span>;
    
    try {
      const data = JSON.parse(content);
      if (data && data.blocks) {
        return (
          <div>
            {data.blocks.map((block, index) => {
              if (block.type === 'paragraph' && block.data && block.data.text) {
                return <p key={index} dangerouslySetInnerHTML={{ __html: block.data.text }} />;
              }
              if (block.type === 'image' && block.data && block.data.url) {
                return <img key={index} src={block.data.url} alt="" style={{ maxWidth: '100%', maxHeight: 300, marginBottom: 8 }} />;
              }
              if (block.type === 'header' && block.data && block.data.text) {
                const Tag = `h${block.data.level || 2}`;
                return <Tag key={index} dangerouslySetInnerHTML={{ __html: block.data.text }} />;
              }
              if (block.type === 'list' && block.data && block.data.items) {
                const ListTag = block.data.style === 'ordered' ? 'ol' : 'ul';
                return (
                  <ListTag key={index}>
                    {block.data.items.map((item, i) => (
                      <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                  </ListTag>
                );
              }
              return null;
            })}
          </div>
        );
      }
    } catch (e) {
      return <div style={{ whiteSpace: 'pre-line' }}>{content}</div>;
    }
    return <span style={{ color: '#999' }}>未填写</span>;
  };



  if (loading) {
    return <div>加载中...</div>;
  }

  if (!process) {
    return <div>流程不存在</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="glass-page-title">{process.name}</h1>
        <p className="glass-page-subtitle">{process.description}</p>
      </div>

      {/* 流程基本信息 */}
      <Card className="glass-card" title="流程基本信息" style={{ marginBottom: 24 }}>
        <Space size="large" wrap>
          <Space>
            <span>状态：</span>
            <Tag color={process.status === 'completed' ? 'green' : 'blue'}>
              {process.status === 'completed' ? '已完成' : '进行中'}
            </Tag>
          </Space>
          <Space>
            <span>创建人：</span>
            <span>{process.creator_name || '未知用户'}</span>
          </Space>
          <Space>
            <span>创建时间：</span>
            <span>{new Date(process.created_at).toLocaleString()}</span>
          </Space>
          {process.completed_at && (
            <Space>
              <span>完成时间：</span>
              <span>{new Date(process.completed_at).toLocaleString()}</span>
            </Space>
          )}
        </Space>
      </Card>

      {/* 阶段进度条 */}
      <Card className="glass-card" title="阶段进度" style={{ marginBottom: 24 }}>
        <div className="glass-progress stage-progress" style={{ marginBottom: 24 }}>
          <Progress percent={calculateProgress()} status="active" />
        </div>
        
        {/* 水平阶段便签 */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 8 }}>
          {process.stages.map((stage, index) => {
            const userData = localStorage.getItem('userInfo');
            const currentUserId = userData ? JSON.parse(userData).id : null;
            const isSelected = currentStage?.id === stage.id;
            
            return (
              <div key={stage.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div
                  style={{
                    padding: '8px 16px',
                    borderRadius: 4,
                    border: `2px solid ${isSelected ? '#1890ff' : stage.status === 'completed' ? '#52c41a' : stage.status === 'in_progress' ? '#108ee9' : 'rgba(255, 255, 255, 0.3)'}`,
                    backgroundColor: isSelected ? '#1890ff' : stage.status === 'completed' ? '#52c41a' : stage.status === 'in_progress' ? '#108ee9' : 'rgba(255, 255, 255, 0.08)',
                    color: isSelected || stage.status === 'completed' || stage.status === 'in_progress' ? '#ffffff' : 'rgba(255, 255, 255, 0.85)',
                    cursor: 'pointer',
                    marginBottom: 8,
                    minWidth: 100,
                    textAlign: 'center',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    boxShadow: isSelected ? '0 0 8px rgba(24,144,255,0.5)' : 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => setCurrentStage(stage)}
                  onDoubleClick={() => handleEditStage(stage)}
                >
                  {stage.name}
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255, 255, 255, 0.5)' }}>
                  {stage.status === 'completed' ? '已完成' : stage.status === 'in_progress' ? '进行中' : '未开始'}
                </div>
                {/* 阶段用户列表 */}
                {stage.users && stage.users.length > 0 && (
                  <div style={{ marginTop: 4, fontSize: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: '100%' }}>
                    {stage.users.map((stageUser, i) => {
                      const isCurrentUser = stageUser.user_id === currentUserId;
                      const isCompleted = stageUser.status === 'completed';
                      
                      return (
                        <div 
                          key={stageUser.id} 
                          style={{
                            padding: '2px 8px',
                            borderRadius: 4,
                            backgroundColor: isCurrentUser ? 'rgba(24, 144, 255, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                            color: isCurrentUser ? '#4F8AFF' : 'rgba(255, 255, 255, 0.65)',
                            width: '100%',
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span>{stageUser.username}</span>
                          {stage.status === 'in_progress' && isCurrentUser && !isCompleted && (
                            <input
                              type="checkbox"
                              checked={isCompleted}
                              onChange={() => handleCompleteStageUser(stageUser.id)}
                              style={{ marginLeft: 8 }}
                            />
                          )}
                          {isCompleted && (
                            <Tag size="small" color="green" style={{ margin: 0, padding: '0 4px' }}>已完成</Tag>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {index < process.stages.length - 1 && (
                  <div style={{ position: 'absolute', top: '50%', left: '100%', transform: 'translateY(-50%)' }}>
                    <RightOutlined style={{ color: 'rgba(255, 255, 255, 0.3)' }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ marginBottom: 16, color: '#ff4d4f', fontSize: 12 }}>
          提示：单击选中阶段，双击编辑阶段信息
        </div>
        
        {/* 阶段详情 */}
        {currentStage && (
          <div style={{ border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 8, padding: 16, marginTop: 16, backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
            <h3 style={{ marginBottom: 16, color: 'rgba(255, 255, 255, 0.85)' }}>阶段详情：{currentStage.name}</h3>
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.65)' }}>状态：</span>
              <Tag color={currentStage.status === 'completed' ? 'green' : 'blue'}>
                {currentStage.status === 'completed' ? '已完成' : '进行中'}
              </Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.65)' }}>相关用户：</span>
              {currentStage.users && currentStage.users.length > 0 ? (
                currentStage.users.map(stageUser => (
                  <Tag key={stageUser.id} style={{ marginLeft: 4 }}>
                    {stageUser.username} {stageUser.status === 'completed' && <Tag size="small" color="green" style={{ marginLeft: 4 }}>已完成</Tag>}
                  </Tag>
                ))
              ) : (
                <span style={{ color: 'rgba(255, 255, 255, 0.4)' }}>未设置</span>
              )}
            </div>
            <div style={{ marginBottom: 16 }}>
              <span style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.65)' }}>阶段信息：</span>
              <Button type="link" size="small" onClick={() => handleEditStage(currentStage)} style={{ padding: '0 8px' }}>
                编辑
              </Button>
              <div style={{ marginTop: 8, padding: 12, border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: 8, backgroundColor: 'rgba(255, 255, 255, 0.03)' }}>
                <EditorReadOnly data={currentStage.content} />
              </div>
            </div>
            {/* 编辑历史 */}
            {currentStage.content_logs && currentStage.content_logs.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontWeight: 'bold', color: 'rgba(255, 255, 255, 0.65)' }}>编辑历史：</span>
                <Collapse
                  ghost
                  defaultActiveKey={['history']}
                  style={{ marginTop: 8 }}
                  items={[
                    {
                      key: 'history',
                      label: `共 ${currentStage.content_logs.length} 条编辑记录`,
                       children: (
                         <div>
                           {currentStage.content_logs.map(log => (
                             <div
                               key={log.id}
                               style={{
                                 marginBottom: 12,
                                 padding: 12,
                                 border: '1px solid rgba(255, 255, 255, 0.1)',
                                 borderRadius: 8,
                                 backgroundColor: 'rgba(255, 255, 255, 0.03)',
                               }}
                             >
                               <div style={{ marginBottom: 8, color: 'rgba(255, 255, 255, 0.65)' }}>
                                 {log.username} 编辑于 {new Date(log.created_at).toLocaleString('zh-CN', { hour12: false })}
                               </div>
                               <EditorReadOnly data={log.content} />
                             </div>
                           ))}
                         </div>
                       ),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        )}
      </Card>

      {/* 当前阶段任务 */}
      <Card className="glass-card" title="当前阶段任务" style={{ marginBottom: 24 }}>
        {process.stages && process.stages.length > 0 ? (
          process.stages.filter(stage => stage.status === 'pending').length > 0 ? (
            <List className="glass-list"
              dataSource={process.stages.find(stage => stage.status === 'pending').tasks || []}
              renderItem={task => (
                <List.Item
                  actions={[
                    <Button type="link" onClick={() => handleViewTask(task.id)}>
                      查看详情
                    </Button>
                  ]}
                >
                  <List.Item.Meta
                    title={task.name}
                    description={task.description}
                  />
                  <Badge status={task.status === 'completed' ? 'success' : 'processing'} />
                </List.Item>
              )}
            />
          ) : (
            <div style={{ color: 'rgba(255, 255, 255, 0.5)' }}>所有任务已完成</div>
          )
        ) : (
          <div style={{ color: 'rgba(255, 255, 255, 0.5)' }}>暂无任务</div>
        )}
      </Card>

      {/* 编辑阶段模态框 */}
      <Modal
        className="glass-modal"
        title={`编辑阶段：${currentStage?.name}`}
        open={editStageModalVisible}
        onCancel={handleEditStageModalClose}
        footer={null}
        width={800}
        destroyOnClose
      >
        <Form
          form={editForm}
          className="glass-form"
          onFinish={handleEditStageSubmit}
          layout="vertical"
        >
          <Form.Item
            name="users"
            label="相关用户"
          >
            <Select
              mode="multiple"
              placeholder="选择相关用户"
              style={{ width: '100%' }}
              options={users.map(user => ({
                value: user.id,
                label: user.username
              }))}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="阶段信息"
          >
            <EditorComponent
              ref={editorRef}
              initialData={editorData}
              onReady={() => {
                console.log('=== Debug: Editor onReady called ===');
                setEditorReady(true);
              }}
            />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button onClick={handleEditStageModalClose}>取消</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                保存
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProcessDetail;